import React, { useState, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    Modal,
    TextField,
    Typography,
    IconButton,
    Tooltip,
    MenuItem
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Currency formatter for display
const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
});

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const TimeCardApprovalPage = () => {
    // Time card data and loading state
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal states for time editing and comment entry
    const [timeEditModalOpen, setTimeEditModalOpen] = useState(false);
    const [commentModalOpen, setCommentModalOpen] = useState(false);

    // Which field (ClockInTime or ClockOutTime) is being edited,
    // the row being edited, and the new datetime value
    const [editingField, setEditingField] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const [newDateTime, setNewDateTime] = useState('');
    const [commentText, setCommentText] = useState('');

    // Filter states: Site, Employee (Name or Number), Month, Week
    const [filterSite, setFilterSite] = useState('');
    const [filterEmployee, setFilterEmployee] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [filterWeek, setFilterWeek] = useState('');

    // New state for PTO Usage modal
    const [ptoModalOpen, setPtoModalOpen] = useState(false);
    const [ptoEmployeeName, setPtoEmployeeName] = useState("");
    const [ptoEmployee, setPtoEmployee] = useState(null);
    // Default date to today in YYYY-MM-DD format
    const [ptoDate, setPtoDate] = useState(new Date().toISOString().slice(0, 10));
    const [ptoHours, setPtoHours] = useState("");

    // New state for the employee list used in the PTO modal
    const [employeeList, setEmployeeList] = useState([]);

    // Create refs for each form field (to avoid re-rendering on every keystroke)
    const employeeNameRef = useRef();
    const employeeNumberRef = useRef();
    const bargainingUnitRef = useRef();
    const positionRef = useRef();
    const statusRef = useRef();
    const effectivePeriodRef = useRef();
    const calCodeRef = useRef();
    const rateTypeRef = useRef();
    const schdRef = useRef();
    const gradeStepRef = useRef();
    const FTERef = useRef();
    const baseRateRef = useRef();
    const adjustedRateRef = useRef();
    const proratedRef = useRef();
    const hoursPerDayRef = useRef();
    const PTOBalanceRef = useRef();

    // API endpoints
    const timecardEndpoint = "https://figazuresql.swancreate.com/api/swa/Timecard";
    const employeeEndpoint = "https://figazuresql.swancreate.com/api/swa/EmployeeTimecard";

    // Third-party PTO form URL (loaded via iframe)
    const thirdPartyFormUrl = "https://liquidoffice.figk-12.com:8443/lfserver/jsf_PTO_Use";

    // On mount, fetch time card entries
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(timecardEndpoint);
                setRows(response.data);
            } catch (error) {
                console.error("Error fetching time card records:", error);
            }
            setLoading(false);
        };
        fetchData();
    }, []);

    // Also fetch employee list for PTO usage and sort alphabetically by EmployeeName
    useEffect(() => {
        const fetchEmployeesForPTO = async () => {
            try {
                const response = await axios.get(employeeEndpoint);
                const sorted = response.data.sort((a, b) =>
                    a.EmployeeName.localeCompare(b.EmployeeName)
                );
                setEmployeeList(sorted);
            } catch (error) {
                console.error("Error fetching employees for PTO:", error);
            }
        };
        fetchEmployeesForPTO();
    }, []);

    // Compute unique Site names for the filter dropdown.
    const uniqueSites = Array.from(new Set(rows.map((row) => row.SiteName)));

    // Helper: Get month string (YYYY-MM) from a date.
    const getMonthString = (date) => {
        const d = new Date(date);
        return d.toISOString().slice(0, 7);
    };

    // Helper: Get week number (1-5) from a date.
    const getWeekNumber = (date) => {
        const d = new Date(date);
        return Math.ceil(d.getDate() / 7).toString();
    };

    // Filter logic for time card entries
    const filteredRows = rows.filter((row) => {
        const matchesSite =
            filterSite === '' ||
            row.SiteName?.toLowerCase().includes(filterSite.toLowerCase());

        const employeeName = row.EmployeeName || '';
        const matchesEmployee =
            filterEmployee === '' ||
            employeeName.toLowerCase().includes(filterEmployee.toLowerCase()) ||
            (row.EmployeeNumber &&
                row.EmployeeNumber.toLowerCase().includes(filterEmployee.toLowerCase()));

        const rowMonth = getMonthString(row.ClockInTime);
        const matchesMonth = filterMonth === '' || rowMonth === filterMonth;

        const rowWeek = getWeekNumber(row.ClockInTime);
        const matchesWeek = filterWeek === '' || rowWeek === filterWeek;

        return matchesSite && matchesEmployee && matchesMonth && matchesWeek;
    });

    // Open time-edit modal
    const handleEditTime = (field, row) => {
        setEditingField(field);
        setSelectedRow(row);
        const dt = new Date(row[field]);
        const formatted = dt.toISOString().slice(0, 16);
        setNewDateTime(formatted);
        setTimeEditModalOpen(true);
    };

    // Confirm time edit
    const handleTimeEditConfirm = () => {
        if (!selectedRow) return;
        const newTime = new Date(newDateTime);

        if (editingField === 'ClockInTime') {
            const currentClockOut = new Date(selectedRow.ClockOutTime);
            if (newTime >= currentClockOut) {
                alert("Clock In Time must be before Clock Out Time.");
                return;
            }
        }
        if (editingField === 'ClockOutTime') {
            const currentClockIn = new Date(selectedRow.ClockInTime);
            if (newTime <= currentClockIn) {
                alert("Clock Out Time must be after Clock In Time.");
                return;
            }
        }

        const updatedRows = rows.map((r) => {
            if (r.TimeCardID === selectedRow.TimeCardID) {
                return {
                    ...r,
                    [editingField]: newTime.toISOString(),
                    edited: true,
                };
            }
            return r;
        });
        setRows(updatedRows);
        setTimeEditModalOpen(false);
        setEditingField('');
        setSelectedRow(null);
    };

    // Open comment modal when Save is clicked
    const handleRowSave = (row) => {
        if (!row.edited) return;
        setSelectedRow(row);
        setCommentText('');
        setCommentModalOpen(true);
    };

    // Save comment and update record
    const handleCommentSave = async () => {
        if (!selectedRow) return;
        const updatedRow = {
            ...selectedRow,
            Comments: commentText,
        };
        const { edited, apiid, ...payload } = updatedRow;
        try {
            await axios.put(
                `https://figazuresql.swancreate.com/api/swa/TimeCard/${selectedRow.apiid}`,
                payload
            );
            const updatedRows = rows.map((r) =>
                r.TimeCardID === selectedRow.TimeCardID ? updatedRow : r
            );
            setRows(updatedRows);
            setCommentModalOpen(false);
            setSelectedRow(null);
        } catch (err) {
            console.error('Error updating time card:', err);
        }
    };

    // Delete a time card entry and update state
    const handleDeleteRow = async (row) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            await axios.delete(`https://figazuresql.swancreate.com/api/swa/TimeCard/${row.apiid}`);
            setRows(rows.filter(r => r.TimeCardID !== row.TimeCardID));
        } catch (error) {
            console.error("Error deleting time card record:", error);
        }
    };

    // Export to Excel
    const handleExportExcel = async () => {
        if (!filteredRows.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Time Cards");

        worksheet.columns = [
            { header: "TimeCardID", key: "TimeCardID", width: 15 },
            { header: "EmployeeNumber", key: "EmployeeNumber", width: 20 },
            { header: "Employee", key: "Employee", width: 30 },
            { header: "SiteName", key: "SiteName", width: 25 },
            { header: "ClockInTime", key: "ClockInTime", width: 30 },
            { header: "ClockOutTime", key: "ClockOutTime", width: 30 },
            { header: "TotalPay", key: "TotalPay", width: 15 },
            { header: "Comments", key: "Comments", width: 30 },
            { header: "ERS", key: "ERS", width: 15 },
            { header: "WorkersComp", key: "WorkersComp", width: 15 },
            { header: "FICA", key: "FICA", width: 15 },
        ];

        filteredRows.forEach((row) => {
            worksheet.addRow({
                TimeCardID: row.TimeCardID,
                EmployeeNumber: row.EmployeeNumber || '',
                Employee: row.EmployeeName || '',
                SiteName: row.SiteName || '',
                ClockInTime: row.ClockInTime ? new Date(row.ClockInTime).toLocaleString() : '',
                ClockOutTime: row.ClockOutTime ? new Date(row.ClockOutTime).toLocaleString() : '',
                TotalPay: row.TotalPay ? Number(row.TotalPay) : 0,
                Comments: row.Comments || '',
                ERS: row.TotalPay ? Number(row.TotalPay) * 0.152 : 0,
                WorkersComp: row.TotalPay ? Number(row.TotalPay) * 0.0547 : 0,
                FICA: row.TotalPay ? Number(row.TotalPay) * 0.0765 : 0,
            });
        });

        const currencyColumns = ["TotalPay", "ERS", "WorkersComp", "FICA"];
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) {
                row.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF1E90FF' },
                    };
                    cell.font = {
                        color: { argb: 'FFFFFFFF' },
                        bold: true,
                    };
                    cell.alignment = { horizontal: 'center' };
                });
            } else {
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    const colKey = worksheet.getColumn(colNumber).key;
                    if (currencyColumns.includes(colKey)) {
                        const val = cell.value;
                        if (typeof val === 'number') {
                            cell.value = currencyFormatter.format(val);
                        } else {
                            cell.value = currencyFormatter.format(0);
                        }
                    }
                });
            }
        });

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), "TimeCards.xlsx");
    };

    // New function: Handle submission of PTO usage.
    // (This function remains in the code but is no longer used because the PTO modal now loads a third-party form.)
    const handlePTOUsage = async (e) => {
        e.preventDefault();
        if (!ptoEmployee) {
            alert("Please select an employee from the list.");
            return;
        }
        const clockIn = new Date(ptoDate + "T07:00:00");
        const clockOut = new Date(clockIn);
        clockOut.setHours(clockOut.getHours() + Number(ptoHours));

        // Updated payload: include UserID using ptoEmployee.apiid
        const newTimeCard = {
            UserID: ptoEmployee.apiid, // use apiid for UserID
            EmployeeName: ptoEmployee.EmployeeName,
            EmployeeNumber: ptoEmployee.EmployeeNumber,
            SiteName: ptoEmployee.SiteName || ptoEmployee.BargainingUnit,
            ClockInTime: clockIn.toISOString(),
            ClockOutTime: clockOut.toISOString(),
            PTOHours: ptoHours
        };

        try {
            await axios.post(timecardEndpoint, newTimeCard);
            const newPTOBalance = Number(ptoEmployee.PTOBalance) - Number(ptoHours);
            const updatedEmployee = {
                ...ptoEmployee,
                PTOBalance: newPTOBalance,
                PTOHours: ptoHours
            };
            // Remove apiid from the payload to prevent duplicate parameter error.
            const { apiid, ...payload } = updatedEmployee;
            await axios.put(`${employeeEndpoint}/${ptoEmployee.apiid}`, payload);
            alert("PTO usage submitted successfully.");
            setPtoModalOpen(false);
        } catch (err) {
            console.error("Error submitting PTO usage:", err);
        }
    };

    // DataGrid columns definition
    const columns = [
        {
            field: 'EmployeeNumber',
            headerName: 'Employee Number',
            width: 150,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 1,
                    }}
                >
                    {params.value || ''}
                </Typography>
            )
        },
        {
            field: 'Employee',
            headerName: 'Employee',
            width: 300,
            valueGetter: (params) => params.row.EmployeeName || '',
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 2,
                    }}
                >
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'SiteName',
            headerName: 'Site',
            width: 300,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 2,
                    }}
                >
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'ClockInTime',
            headerName: 'Clock In',
            width: 250,
            renderCell: (params) => {
                const dt = new Date(params.value);
                const formatted = dt.toLocaleString();
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            pl: 2,
                        }}
                        onClick={() => handleEditTime('ClockInTime', params.row)}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                color: '#ADD8E6',
                            }}
                        >
                            {formatted}
                        </Typography>
                        <EditIcon fontSize="small" sx={{ ml: 1, color: '#ADD8E6' }} />
                    </Box>
                );
            }
        },
        {
            field: 'ClockOutTime',
            headerName: 'Clock Out',
            width: 250,
            renderCell: (params) => {
                const dt = new Date(params.value);
                const formatted = dt.toLocaleString();
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            pl: 2,
                        }}
                        onClick={() => handleEditTime('ClockOutTime', params.row)}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                color: '#ADD8E6',
                            }}
                        >
                            {formatted}
                        </Typography>
                        <EditIcon fontSize="small" sx={{ ml: 1, color: '#ADD8E6' }} />
                    </Box>
                );
            }
        },
        {
            field: 'TotalPay',
            headerName: 'Total Pay',
            width: 150,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 1,
                    }}
                >
                    {params.value ? currencyFormatter.format(Number(params.value)) : currencyFormatter.format(0)}
                </Typography>
            )
        },
        {
            field: 'Comments',
            headerName: 'Comments',
            width: 300,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 2,
                    }}
                >
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'ERS',
            headerName: 'ERS',
            width: 120,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 1,
                    }}
                >
                    {params.value || (params.row.TotalPay ? currencyFormatter.format(parseFloat(params.row.TotalPay) * 0.152) : currencyFormatter.format(0))}
                </Typography>
            )
        },
        {
            field: 'WorkersComp',
            headerName: 'WorkersComp',
            width: 120,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 1,
                    }}
                >
                    {params.value || (params.row.TotalPay ? currencyFormatter.format(parseFloat(params.row.TotalPay) * 0.0547) : currencyFormatter.format(0))}
                </Typography>
            )
        },
        {
            field: 'FICA',
            headerName: 'FICA',
            width: 120,
            renderCell: (params) => (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        color: '#ADD8E6',
                        pl: 1,
                    }}
                >
                    {params.value || (params.row.TotalPay ? currencyFormatter.format(parseFloat(params.row.TotalPay) * 0.0765) : currencyFormatter.format(0))}
                </Typography>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            renderCell: (params) => (
                <Tooltip title="Save changes">
                    <span>
                        <IconButton
                            onClick={() => handleRowSave(params.row)}
                            disabled={!params.row.edited}
                            sx={{ color: '#ADD8E6' }}
                        >
                            <SaveIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'delete',
            headerName: 'Delete',
            width: 100,
            renderCell: (params) => (
                <Tooltip title="Delete record">
                    <span>
                        <IconButton
                            onClick={() => handleDeleteRow(params.row)}
                            sx={{ color: '#ADD8E6' }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        }
    ];

    return (
        <Box
            sx={{
                height: 1000,
                width: '100%',
                p: 2,
                backgroundColor: '#1A1A2E'
            }}
        >
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem', color: '#ADD8E6' }} gutterBottom>
                Time Card Approval & Editing
            </Typography>

            {/* Filter Section */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                    select
                    label="Filter by Site"
                    value={filterSite}
                    onChange={(e) => setFilterSite(e.target.value)}
                    variant="outlined"
                    size="medium"
                    sx={{
                        backgroundColor: '#1A1A2E',
                        width: 250,
                        '& .MuiInputBase-input': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        },
                        '& .MuiInputLabel-root': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        }
                    }}
                >
                    <MenuItem value="">All Sites</MenuItem>
                    {uniqueSites.map((site) => (
                        <MenuItem key={site} value={site}>
                            {site}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Employee Name or Employee Number"
                    value={filterEmployee}
                    onChange={(e) => setFilterEmployee(e.target.value)}
                    variant="outlined"
                    size="medium"
                    sx={{
                        backgroundColor: '#1A1A2E',
                        '& .MuiInputBase-input': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        },
                        '& .MuiInputLabel-root': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        }
                    }}
                />

                <TextField
                    label="Filter by Month"
                    type="month"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    variant="outlined"
                    size="medium"
                    sx={{
                        backgroundColor: '#1A1A2E',
                        width: 200,
                        '& .MuiInputBase-input': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        },
                        '& .MuiInputLabel-root': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        }
                    }}
                />

                <TextField
                    select
                    label="Filter by Week"
                    value={filterWeek}
                    onChange={(e) => setFilterWeek(e.target.value)}
                    variant="outlined"
                    size="medium"
                    sx={{
                        backgroundColor: '#1A1A2E',
                        width: 200,
                        '& .MuiInputBase-input': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        },
                        '& .MuiInputLabel-root': {
                            color: '#ADD8E6',
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                        }
                    }}
                >
                    <MenuItem value="">All Weeks</MenuItem>
                    <MenuItem value="1">Week 1</MenuItem>
                    <MenuItem value="2">Week 2</MenuItem>
                    <MenuItem value="3">Week 3</MenuItem>
                    <MenuItem value="4">Week 4</MenuItem>
                    <MenuItem value="5">Week 5</MenuItem>
                </TextField>

                <Button
                    variant="contained"
                    color="success"
                    onClick={handleExportExcel}
                    sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}
                >
                    Export Excel
                </Button>

                {/* NEW: Button to Use PTO Hours */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setPtoModalOpen(true)}
                    sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}
                >
                    Use PTO Hours
                </Button>
            </Box>

            <DataGrid
                rows={filteredRows}
                getRowId={(row) => row.TimeCardID}
                columns={columns}
                loading={loading}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                sortModel={[{ field: 'ClockInTime', sort: 'desc' }]}
                rowHeight={50}
                headerHeight={60}
                sx={{
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ADD8E6',
                    backgroundColor: '#16213E',
                    fontSize: '0.8rem',
                    '& .MuiDataGrid-cell': { fontSize: '0.8rem' },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#1A1A2E',
                        borderBottom: '2px solid #0B0D2A'
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#1A1A2E' },
                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#16213E' },
                    '& .MuiDataGrid-footerContainer': { backgroundColor: '#1A1A2E' },
                }}
            />

            {/* Modal for editing date/time */}
            <Modal open={timeEditModalOpen} onClose={() => setTimeEditModalOpen(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#000' }} gutterBottom>
                        Edit {editingField === 'ClockInTime' ? 'Clock In' : 'Clock Out'} Time
                    </Typography>
                    <TextField
                        label="Select Date and Time"
                        type="datetime-local"
                        value={newDateTime}
                        onChange={(e) => setNewDateTime(e.target.value)}
                        fullWidth
                        InputLabelProps={{
                            shrink: true,
                            style: { fontWeight: 'bold', fontSize: '1.2rem' }
                        }}
                        sx={{ '& .MuiInputBase-input': { fontWeight: 'bold', fontSize: '1.2rem' } }}
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={() => setTimeEditModalOpen(false)}
                            sx={{ mr: 1, fontWeight: 'bold', fontSize: '1.2rem' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleTimeEditConfirm}
                            sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}
                        >
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Modal for entering comments */}
            <Modal open={commentModalOpen} onClose={() => setCommentModalOpen(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#000' }} gutterBottom>
                        Enter Comments for the Change
                    </Typography>
                    <TextField
                        label="Comments"
                        multiline
                        rows={4}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        fullWidth
                        InputLabelProps={{
                            style: { fontWeight: 'bold', fontSize: '1.2rem' }
                        }}
                        sx={{ '& .MuiInputBase-input': { fontWeight: 'bold', fontSize: '1.2rem' } }}
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={() => setCommentModalOpen(false)}
                            sx={{ mr: 1, fontWeight: 'bold', fontSize: '1.2rem' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCommentSave}
                            sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Modal for PTO Usage - Updated to load a third-party form in an iframe */}
            <Modal open={ptoModalOpen} onClose={() => setPtoModalOpen(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '5in',
                        height: '5in',
                        bgcolor: '#1A1A2E',
                        color: '#ffffff',
                        border: '2px solid #ffffff',
                        boxShadow: 24,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                        <IconButton onClick={() => setPtoModalOpen(false)} sx={{ color: '#ffffff' }}>
                            X
                        </IconButton>
                    </Box>
                    <iframe
                        src={thirdPartyFormUrl}
                        title="Third Party PTO Form"
                        style={{ flex: 1, border: 'none' }}
                    />
                </Box>
            </Modal>
        </Box>
    );
};

export default TimeCardApprovalPage;
